import { FileAttachmentConfig } from '../types/fileAttachments.types';

export const FILE_ATTACHMENT_CONSTANTS = {
    // Default configuration
    DEFAULT_CONFIG: {
        maxAttachments: 10,
        maxFileSize: 10 * 1024 * 1024, // 10MB
        allowedFileTypes: ['.pdf', '.md', '.txt', '.jpg', '.jpeg', '.png'],
        allowedMimeTypes: [
            'image/jpeg',
            'image/png',
            'image/jpg',
            'application/pdf',
            'text/markdown',
            'text/x-markdown',
            'text/md',
            'text/plain',
        ],
    } as FileAttachmentConfig,

    // File size limits
    FILE_SIZE_LIMITS: {
        MAX_SIZE: 10 * 1024 * 1024, // 10MB
        WARNING_SIZE: 5 * 1024 * 1024, // 5MB
    },

    // Error messages
    ERROR_MESSAGES: {
        FILE_TOO_LARGE: (filename: string, maxSize: string) =>
            `${filename} is too large. Each file must be ${maxSize} or smaller.`,
        MAX_ATTACHMENTS_REACHED: (maxCount: number) => `You can only upload ${maxCount} files at a time.`,
        INVALID_FILE_TYPE: (filename: string) => `${filename} is not a supported file type.`,
        DUPLICATE_FILE: (filename: string) => `${filename} has already been added.`,
        UPLOAD_FAILED: 'Failed to upload file. Please try again.',
        NETWORK_ERROR: 'Network error. Please check your connection.',
    },

    // Success messages
    SUCCESS_MESSAGES: {
        FILE_ADDED: (filename: string) => `${filename} added successfully.`,
        FILES_CLEARED: 'All attachments cleared.',
    },

    // UI constants
    UI: {
        PREVIEW_SIZES: {
            small: 'size-16',
            medium: 'size-20',
            large: 'size-24',
        },
        CARD_SIZES: {
            small: 'max-w-xs',
            medium: 'max-w-sm',
            large: 'max-w-md',
        },
    },

    // File type categories
    FILE_TYPE_CATEGORIES: {
        IMAGE: ['image/jpeg', 'image/png', 'image/jpg'],
        DOCUMENT: ['application/pdf', 'text/markdown', 'text/x-markdown', 'text/md', 'text/plain'],
    },

    // Validation rules
    VALIDATION: {
        MIN_FILE_SIZE: 1, // 1 byte
        MAX_FILENAME_LENGTH: 255,
        ALLOWED_EXTENSIONS: ['.pdf', '.md', '.txt', '.jpg', '.jpeg', '.png'],
    },
} as const;

export const getFileSizeString = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileTypeCategory = (mimeType: string): 'image' | 'document' | 'unknown' => {
    if ((FILE_ATTACHMENT_CONSTANTS.FILE_TYPE_CATEGORIES.IMAGE as readonly string[]).includes(mimeType)) {
        return 'image';
    }
    if ((FILE_ATTACHMENT_CONSTANTS.FILE_TYPE_CATEGORIES.DOCUMENT as readonly string[]).includes(mimeType)) {
        return 'document';
    }
    return 'unknown';
};

export const isImageFile = (file: File): boolean => {
    return (FILE_ATTACHMENT_CONSTANTS.FILE_TYPE_CATEGORIES.IMAGE as readonly string[]).includes(file.type);
};

export const isDocumentFile = (file: File): boolean => {
    return (FILE_ATTACHMENT_CONSTANTS.FILE_TYPE_CATEGORIES.DOCUMENT as readonly string[]).includes(file.type);
};
